/**
 * Autore: Salvo de Luca
 * Azienda: Dialogicom s.r.l.s.
 * File: FaultDescriptionCustom.tsx
 * Release: v1.0.0
 * Descrizione: Componente custom per descrizione guasti del cliente - stile modalità consegna/pagamento
 */

import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Wrench, 
  Volume2,
  Droplets,
  Cog,
  Zap,
  TrendingDown,
  AlertTriangle,
  Cloud,
  Lock
} from 'lucide-react';

interface FaultDescriptionCustomProps {
  value?: string[] | any;
  onChange?: (value: string[]) => void;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
}

const FAULT_OPTIONS = [
  { id: 'rotta', label: 'Rotta', icon: Wrench },
  { id: 'fischio', label: 'Fischio', icon: Volume2 },
  { id: 'consumo_olio', label: 'Consumo Olio', icon: Droplets },
  { id: 'motore_turbina', label: 'Motore o Turbina', icon: Cog },
  { id: 'coppia_alta', label: 'Coppia Alta', icon: Zap },
  { id: 'scarsa_resa', label: 'Scarsa resa', icon: TrendingDown },
  { id: 'sovrappressione', label: 'Sovrappressione', icon: AlertTriangle },
  { id: 'fumo_bianco', label: 'Fumo bianco', icon: Cloud },
  { id: 'variabile_bloccata', label: 'Variabile bloccata', icon: Lock }
];

export const FaultDescriptionCustom: React.FC<FaultDescriptionCustomProps> = ({
  value,
  onChange,
  disabled = false
}) => {
  // DEBUG LOG
  console.log('🔧 [FaultDescriptionCustom] Value ricevuto:', value);
  
  // Gestisce sia array semplici che valori diversi
  const [selectedFaults, setSelectedFaults] = useState<string[]>(() => {
    if (Array.isArray(value)) {
      console.log('🔧 [FaultDescriptionCustom] Value è array:', value);
      return value; // Array semplice
    }
    console.log('🔧 [FaultDescriptionCustom] Value non valido, usando default []');
    return []; // Default
  });

  // Sincronizza lo stato quando il value cambia
  useEffect(() => {
    if (value) {
      console.log('🔧 [FaultDescriptionCustom] Aggiornamento value:', value);
      if (Array.isArray(value)) {
        setSelectedFaults(value);
      }
    }
  }, [value]);

  const handleFaultToggle = (faultId: string) => {
    if (disabled) return;

    const newFaults = selectedFaults.includes(faultId)
      ? selectedFaults.filter(id => id !== faultId)
      : [...selectedFaults, faultId];

    setSelectedFaults(newFaults);
    onChange?.(newFaults);
  };

  return (
    <div className="space-y-4">
      <div className="space-y-4">
        {/* Opzioni di guasto */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {FAULT_OPTIONS.map((option) => {
            const Icon = option.icon;
            const isSelected = selectedFaults.includes(option.id);
            
            return (
              <motion.div
                key={option.id}
                className={`
                  p-3 rounded-lg border cursor-pointer transition-all
                  ${isSelected 
                    ? 'border-red-500 bg-red-500/10' 
                    : 'border-zinc-600 bg-zinc-700/50 hover:border-zinc-500'
                  }
                  ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
                `}
                onClick={() => handleFaultToggle(option.id)}
                whileHover={!disabled ? { scale: 1.02 } : {}}
                whileTap={!disabled ? { scale: 0.98 } : {}}
              >
                <div className="flex items-center gap-3">
                  <Icon className={`w-5 h-5 ${
                    isSelected ? 'text-red-400' : 'text-gray-400'
                  }`} />
                  <span className={`text-sm font-medium ${
                    isSelected ? 'text-white' : 'text-gray-300'
                  }`}>
                    {option.label}
                  </span>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Info selezionati */}
        {selectedFaults.length > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-sm text-gray-400"
          >
            Selezionati: {selectedFaults.length} guasto{selectedFaults.length > 1 ? 'i' : ''}
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default FaultDescriptionCustom;